/**
 * SpacetimeDB connection — builds the typed DbConnection and persists the auth
 * token so a reload resumes the same identity (the Atlas Passport is durable).
 */

import { DbConnection, type ErrorContext } from '../module_bindings/index.js';
import { installFastFind } from '../lib/fast_find.js';
import type { Identity } from 'spacetimedb';
import { STDB_WS_URI, STDB_DB_NAME, TOKEN_KEY } from '../config.js';

export interface ConnectionCallbacks {
  onConnect: (conn: DbConnection, identity: Identity, token: string) => void;
  onConnectError?: (err: Error) => void;
  onDisconnect?: (err?: Error) => void;
}

/**
 * Build and initiate a SpacetimeDB connection. The returned object is not yet
 * connected — `onConnect` fires once the handshake completes.
 */
export function connect(cb: ConnectionCallbacks): DbConnection {
  const savedToken = localStorage.getItem(TOKEN_KEY) ?? undefined;

  const conn = DbConnection.builder()
    .withUri(STDB_WS_URI)
    .withDatabaseName(STDB_DB_NAME)
    .withToken(savedToken)
    .onConnect((conn, identity, token) => {
      // Persist the token so the next session resumes this identity.
      localStorage.setItem(TOKEN_KEY, token);
      cb.onConnect(conn, identity, token);
    })
    .onConnectError((_ctx: ErrorContext, err: Error) => {
      cb.onConnectError?.(err);
    })
    .onDisconnect((_ctx: ErrorContext, err?: Error) => {
      cb.onDisconnect?.(err);
    })
    .build();
  // REAL INDEXES (2026-09-20): the SDK's `find` / `filter` accessors scan the whole table per call (1.9 ms on the
  // 11.7k-row versona table); 180 call sites assume they are lookups. Patched once, here, before any module reads.
  installFastFind(conn);
  return conn;
}
